import { useEffect, useState } from "react";
import { X, Check, ArrowLeft, ShoppingCartSimple, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { addToCart } from "@/lib/cart";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * BuildBundleModal — Slot-by-slot picker.
 * Walks each slot: pick product → pick size/variant → advance.
 * On finish, POSTs to /api/bundles/quote, gets back pro-rated cart items,
 * appends every line (including the free sticker) to localStorage cart.
 */
export default function BuildBundleModal({ bundle, catalog, onClose, onAdded }) {
  const slots = bundle.slots || [];
  const [step, setStep] = useState(0); // slot index
  const [picks, setPicks] = useState({}); // {slot_key: {product_id, sync_variant_id, product_name, variant_name, image}}
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const currentSlot = slots[step];
  const isLast = step === slots.length - 1;
  const totalSteps = slots.length;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const onPickProduct = (pid, product) => {
    // If product has only one variant, auto-pick it. Otherwise show variant list.
    const variants = product?.variants || [];
    if (variants.length === 1) {
      const v = variants[0];
      setPicks((p) => ({ ...p, [currentSlot.key]: {
        product_id: pid, sync_variant_id: v.id, variant_id: v.variant_id,
        product_name: product.name, variant_name: v.name || "", image: v.preview_url || product.thumbnail_url || "",
        retail_price: Number(v.retail_price || 0),
      }}));
      // Advance
      if (!isLast) setStep(step + 1);
    } else {
      setPicks((p) => ({ ...p, [currentSlot.key]: {
        product_id: pid, sync_variant_id: null, variant_id: null,
        product_name: product.name, variant_name: "", image: product.thumbnail_url || "",
        retail_price: null,
      }}));
    }
  };

  const onPickVariant = (v) => {
    const cur = picks[currentSlot.key];
    setPicks((p) => ({ ...p, [currentSlot.key]: {
      ...cur, sync_variant_id: v.id, variant_id: v.variant_id,
      variant_name: v.name || "", image: v.preview_url || cur.image,
      retail_price: Number(v.retail_price || 0),
    }}));
  };

  const canAdvance = !!(picks[currentSlot?.key]?.sync_variant_id);

  const submitQuote = async () => {
    setSubmitting(true); setErr("");
    try {
      const selections = slots.map((s) => {
        const p = picks[s.key];
        return { slot_key: s.key, product_id: Number(p.product_id), sync_variant_id: Number(p.sync_variant_id) };
      });
      const r = await fetch(`${BACKEND}/api/bundles/quote`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: bundle.slug, selections }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      const d = await r.json();
      // Push every line into localStorage cart
      for (const li of d.line_items) {
        addToCart({
          product_id: li.product_id, product_name: li.product_name,
          sync_variant_id: li.sync_variant_id, variant_id: li.variant_id,
          variant_name: li.variant_name, quantity: 1,
          item_retail_price: li.item_retail_price, image: li.image,
          bundle_slug: li.bundle_slug, bundle_slot_key: li.bundle_slot_key,
          is_free_gift: !!li.is_free_gift,
          is_bundle_free_item: !!li.is_bundle_free_item,
          bundle_coupon_code: li.bundle_coupon_code || d.coupon_code,
          bundle_name: d.bundle_name,
          bundle_price: d.bundle_price,
          bundle_savings: d.savings,
          bundle_original_subtotal: d.original_subtotal,
        });
      }
      if (onAdded) onAdded(d);
      onClose();
    } catch (e) {
      setErr(e.message || "Could not build the bundle. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div role="dialog" aria-label={`Build ${bundle.name}`} data-testid="build-bundle-modal" className="fixed inset-0 z-[90] flex items-stretch md:items-center md:justify-center">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div className="relative w-full md:max-w-3xl md:max-h-[90vh] h-full md:h-auto bg-[#0A0A0A] border-2 border-[#B32027] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/10 p-5 flex items-center gap-3">
          <button type="button" onClick={onClose} data-testid="build-bundle-close" aria-label="Close" className="w-9 h-9 flex items-center justify-center border border-white/20 hover:border-[#B32027] hover:text-[#B32027]">
            <X size={16} weight="bold" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027] truncate">/ Building · {bundle.name}</p>
            <p className="text-white text-sm font-display font-black uppercase tracking-tight mt-0.5 truncate">Step {step + 1} of {totalSteps} · {currentSlot?.label}</p>
          </div>
          <p className="text-white text-lg md:text-2xl font-bold whitespace-nowrap">
            {bundle.mode === "lowest_free" ? "Lowest FREE" : `$${bundle.price}`}
          </p>
        </div>

        {/* Mode hint for lowest_free */}
        {bundle.mode === "lowest_free" && step === 0 && (
          <div className="px-5 pt-4">
            <p className="border border-[#B32027] bg-[#B32027]/10 text-white text-xs p-3 leading-relaxed">
              <strong>Pick any 5 eligible items.</strong> The lowest-priced item is FREE. Auto-applied coupon: <span className="font-mono text-[#B32027]">{bundle.coupon_code}</span>. Plus a free 3-inch sticker.
            </p>
          </div>
        )}

        {/* progress dots */}
        <div className="px-5 pt-4 flex gap-1.5">
          {slots.map((s, i) => (
            <span key={s.key} className={`flex-1 h-1 ${i < step ? "bg-[#B32027]" : i === step ? "bg-[#B32027]/60" : "bg-white/15"}`} />
          ))}
        </div>

        <div className="p-5 space-y-4">
          {err && <p className="border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm">{err}</p>}

          {/* Product picker */}
          {!picks[currentSlot.key]?.product_id && (
            <>
              <p className="text-white/70 text-xs uppercase tracking-[0.25em] font-bold mb-2">Choose your design</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {currentSlot.eligible_products.map((pid) => {
                  const product = catalog[pid] || catalog[String(pid)] || {};
                  if (!product.name) return null;
                  return (
                    <button
                      key={pid}
                      type="button"
                      data-testid={`bundle-pick-product-${pid}`}
                      onClick={() => onPickProduct(pid, product)}
                      className="group border-2 border-white/15 hover:border-[#B32027] bg-[#0F0F0F] text-left overflow-hidden transition-colors"
                    >
                      <div className="aspect-square bg-[#141414] overflow-hidden">
                        {product.thumbnail_url && (
                          <img src={product.thumbnail_url} alt={product.name} loading="lazy" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-white text-xs font-display font-black uppercase tracking-tight leading-tight line-clamp-2 min-h-[2.5rem]">{product.name}</p>
                        {product.price_from != null && (
                          <p className="text-white/50 text-[10px] uppercase tracking-[0.2em] mt-1">Retail ${Math.round(product.price_from)}{product.price_to && product.price_to !== product.price_from ? `–$${Math.round(product.price_to)}` : ""}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Variant picker (size / color) */}
          {picks[currentSlot.key]?.product_id && !picks[currentSlot.key]?.sync_variant_id && (
            <>
              <button
                type="button"
                onClick={() => setPicks((p) => { const n = {...p}; delete n[currentSlot.key]; return n; })}
                className="inline-flex items-center gap-1 text-white/50 hover:text-white text-[10px] uppercase tracking-[0.25em] font-bold"
              >
                <ArrowLeft size={12} weight="bold" /> Change design
              </button>
              <p className="text-white/70 text-xs uppercase tracking-[0.25em] font-bold mb-2 mt-2">Pick your size / variant</p>
              <VariantGrid
                product={catalog[picks[currentSlot.key].product_id] || catalog[String(picks[currentSlot.key].product_id)]}
                allowedVariantIds={(() => {
                  // If the admin restricted variants for this product in this slot,
                  // only show the allowed ones. Empty/missing = all variants allowed.
                  const vr = currentSlot.variant_restrictions || {};
                  const pid = String(picks[currentSlot.key].product_id);
                  const list = vr[pid] || vr[Number(pid)] || null;
                  return list && list.length ? new Set(list.map(Number)) : null;
                })()}
                onPick={onPickVariant}
              />
            </>
          )}

          {/* Selected — go to next slot */}
          {picks[currentSlot.key]?.sync_variant_id && (
            <div className="border-2 border-[#B32027] bg-[#B32027]/10 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Check size={20} weight="bold" className="text-[#B32027]" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-display font-black uppercase text-sm tracking-tight truncate">{picks[currentSlot.key].product_name}</p>
                  {picks[currentSlot.key].variant_name && (
                    <p className="text-white/70 text-xs mt-0.5 truncate">{picks[currentSlot.key].variant_name}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPicks((p) => { const n = {...p}; delete n[currentSlot.key]; return n; })}
                  className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/60 hover:text-white border border-white/20 px-3 py-2"
                >
                  Change
                </button>
                {!isLast ? (
                  <button
                    type="button"
                    data-testid="build-bundle-next"
                    disabled={!canAdvance}
                    onClick={() => setStep(step + 1)}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white text-[10px] font-bold uppercase tracking-[0.25em] px-4 py-3 disabled:opacity-50"
                  >
                    Next: {slots[step + 1]?.label} <ArrowRight size={12} weight="bold" />
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="build-bundle-add-to-cart"
                    disabled={submitting || !canAdvance}
                    onClick={submitQuote}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white text-[10px] font-bold uppercase tracking-[0.25em] px-4 py-3 disabled:opacity-50"
                  >
                    {submitting ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : <ShoppingCartSimple size={12} weight="bold" />}
                    Add bundle to cart · ${bundle.price}
                  </button>
                )}
              </div>
            </div>
          )}

          <p className="text-white/45 text-[11px] leading-relaxed pt-2 border-t border-white/10">
            Bundle products are made on demand and may arrive in separate packages. Free 3-inch sticker included at checkout.
          </p>
        </div>
      </div>
    </div>
  );
}

function VariantGrid({ product, onPick, allowedVariantIds }) {
  if (!product?.variants?.length) return <p className="text-white/50 text-sm italic">No variants available.</p>;
  const variants = allowedVariantIds
    ? product.variants.filter((v) => allowedVariantIds.has(Number(v.id)))
    : product.variants;
  if (!variants.length) return <p className="text-white/50 text-sm italic">No variants available for this pick.</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[45vh] overflow-y-auto">
      {variants.map((v) => (
        <button
          key={v.id}
          type="button"
          data-testid={`bundle-pick-variant-${v.id}`}
          onClick={() => onPick(v)}
          className="border-2 border-white/15 hover:border-[#B32027] bg-[#0F0F0F] p-2 text-left transition-colors"
        >
          <div className="aspect-square bg-[#141414] overflow-hidden mb-2">
            {v.preview_url && <img src={v.preview_url} alt={v.name || "variant"} loading="lazy" className="w-full h-full object-cover" />}
          </div>
          <p className="text-white text-[11px] font-bold leading-tight line-clamp-2">{v.name}</p>
          <p className="text-white/50 text-[10px] mt-0.5">${Number(v.retail_price || 0).toFixed(0)}</p>
        </button>
      ))}
    </div>
  );
}
